import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { FileText, AlertTriangle, Calendar } from 'lucide-react';
import { apiClient } from '../../utils/apiHelper';
import { formatDate } from '../../utils/dateTimeUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const HostelDamageFine = () => {
  const [damages, setDamages] = useState([]);
  const [fines, setFines] = useState([]);
  const [hostels, setHostels] = useState([]);
  const [selectedHostel, setSelectedHostel] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => { 
    fetchHostels();
    fetchData(); 
  }, []);

  const fetchHostels = async () => {
    try {
      const response = await apiClient.get('/api/hostel/hostels');
      setHostels(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch both damages and fines
      const [damagesRes, finesRes] = await Promise.all([
        apiClient.get('/api/hostel/damages'),
        apiClient.get('/api/hostel/fines')
      ]);
      
      const damagesData = Array.isArray(damagesRes.data) ? damagesRes.data : [];
      const finesData = Array.isArray(finesRes.data) ? finesRes.data : [];
      
      // Filter damage-related fines
      const damageFines = finesData.filter(f => 
        f.fine_reason?.toLowerCase().includes('damage') || 
        f.damage_id
      );
      
      setDamages(damagesData);
      setFines(damageFines);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter data based on selections
  const getFilteredData = () => {
    let filtered = [...damages];
    
    // Filter by hostel
    if (selectedHostel && selectedHostel !== 'all') {
      filtered = filtered.filter(d => d.hostel_id === selectedHostel);
    }
    
    // Filter by date range
    if (startDate) {
      filtered = filtered.filter(d => d.damage_date >= startDate);
    }
    if (endDate) {
      filtered = filtered.filter(d => d.damage_date <= endDate);
    }
    
    return filtered;
  };

  const filteredDamages = getFilteredData();
  
  // Calculate summary
  const totalDamages = filteredDamages.length;
  const totalFineAmount = filteredDamages.reduce((sum, d) => sum + (d.damage_cost || 0), 0);
  const paidDamages = filteredDamages.filter(d => d.status === 'Paid');
  const collected = paidDamages.reduce((sum, d) => sum + (d.damage_cost || 0), 0);
  const pending = totalFineAmount - collected;

  return (
    <div className="p-6 space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-orange-600" />
            Hostel Damage Fine Report
          </CardTitle>
          <div className="flex gap-2 items-center flex-wrap">
            <Select value={selectedHostel} onValueChange={setSelectedHostel}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="All Hostels" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Hostels</SelectItem>
                {hostels.map((h) => (
                  <SelectItem key={h.id} value={h.id}>{h.hostel_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-gray-500" />
              <Input 
                type="date" 
                value={startDate} 
                onChange={(e) => setStartDate(e.target.value)} 
                className="w-36"
              />
              <span className="text-gray-500">to</span>
              <Input 
                type="date" 
                value={endDate} 
                onChange={(e) => setEndDate(e.target.value)} 
                className="w-36"
              />
            </div>
            <Button onClick={fetchData} disabled={loading}>
              <FileText className="w-4 h-4 mr-2" />{loading ? 'Loading...' : 'Generate'}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-4 gap-4 mb-6">
            <div className="p-4 bg-red-50 rounded-lg border border-red-200">
              <p className="text-sm text-red-600">Total Damages</p>
              <p className="text-2xl font-bold text-red-700">{totalDamages}</p>
            </div>
            <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
              <p className="text-sm text-orange-600">Total Fine Amount</p>
              <p className="text-2xl font-bold text-orange-700">₹{totalFineAmount.toLocaleString('en-IN')}</p>
            </div>
            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
              <p className="text-sm text-green-600">Collected</p>
              <p className="text-2xl font-bold text-green-700">₹{collected.toLocaleString('en-IN')}</p>
            </div>
            <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
              <p className="text-sm text-yellow-600">Pending</p>
              <p className="text-2xl font-bold text-yellow-700">₹{pending.toLocaleString('en-IN')}</p>
            </div>
          </div>

          <div className="text-sm text-gray-500 mb-2">
            Showing {filteredDamages.length} damage records
          </div>

          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="text-left p-3">Damage #</th>
                  <th className="text-left p-3">Date</th>
                  <th className="text-left p-3">Hostel</th>
                  <th className="text-left p-3">Room</th>
                  <th className="text-left p-3">Student</th>
                  <th className="text-left p-3">Description</th>
                  <th className="text-right p-3">Fine Amount</th>
                  <th className="text-center p-3">Payment Mode</th>
                  <th className="text-center p-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredDamages.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="text-center p-8 text-gray-500">
                      No hostel damage fines recorded.
                    </td>
                  </tr>
                ) : (
                  filteredDamages.map((damage, idx) => (
                    <tr key={idx} className="border-b hover:bg-gray-50">
                      <td className="p-3 font-medium">{damage.damage_number}</td>
                      <td className="p-3">{formatDate(damage.damage_date)}</td>
                      <td className="p-3">{damage.hostel_name}</td>
                      <td className="p-3">{damage.room_number}</td>
                      <td className="p-3">{damage.student_name || '-'}</td>
                      <td className="p-3 max-w-xs truncate">{damage.damage_description}</td>
                      <td className="p-3 text-right text-red-600 font-semibold">
                        ₹{damage.damage_cost?.toLocaleString('en-IN')}
                      </td>
                      <td className="p-3 text-center text-sm text-gray-600">
                        {damage.payment_mode || '-'}
                      </td>
                      <td className="p-3 text-center">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          damage.status === 'Paid' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {damage.status === 'Paid' ? 'Paid' : 'Pending'}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Summary by Hostel */}
          {hostels.length > 0 && (
            <div className="mt-6 border-t pt-4">
              <h3 className="font-semibold mb-3">Summary by Hostel</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {hostels.map(hostel => {
                  const hostelDamages = damages.filter(d => d.hostel_id === hostel.id);
                  const hostelTotal = hostelDamages.reduce((s, d) => s + (d.damage_cost || 0), 0);
                  const hostelPaid = hostelDamages.filter(d => d.status === 'Paid').reduce((s, d) => s + (d.damage_cost || 0), 0);
                  return (
                    <div key={hostel.id} className="p-3 bg-gray-50 rounded-lg text-sm">
                      <p className="font-medium">{hostel.hostel_name}</p>
                      <p className="text-gray-600">Damages: {hostelDamages.length}</p>
                      <p className="text-red-600">Total: ₹{hostelTotal.toLocaleString('en-IN')}</p>
                      <p className="text-green-600">Paid: ₹{hostelPaid.toLocaleString('en-IN')}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default HostelDamageFine;
